/**
 * FILE: packages/supabase/src/services/ops.ts
 * PURPOSE: Supabase service layer — database access and business logic.
 * @deprecated Use domain-specific subpaths instead. This file will be removed in Phase 4.
 * AGENT_CONTEXT: All DB access goes through service layer. Call logActivity() on writes.
 * INVARIANTS: Soft-delete filter, phone PK protection, activity logging.
 * LAST_REVIEWED: 2026-05-07
 */


import { logActivity, withActivityActor } from '../internal/activity'
import type { ActivityActor } from '../internal/activity'
import type { Database } from '../types'
import type { DbClient } from "../dbClient";
import { defaultDbClient } from "../dbClient";
import type { Json, TablesInsert } from '../database.gen'

type OpsAlertInstanceRow = Database['public']['Tables']['ops_alert_instances']['Row']
type BulkOperationRow = Database['public']['Tables']['bulk_operations']['Row']
type SystemHealthSnapshotRow = Database['public']['Tables']['system_health_snapshots']['Row']

export interface DashboardStatsV2 {
  totalStudents: number
  activeStudents: number
  expiringIn30: number
  expiringIn7: number
  enrolledThisMonth: number
  enrolledLastMonth: number
  incompleteCount: number
  incompleteMissingName: number
  incompleteMissingCourse: number
  incompleteMissingDate: number
  needsReviewCount: number
  activeJobs: number
  filledJobs: number
  onHoldJobs: number
  applicationsThisMonth: number
  placementsThisYear: number
  totalCandidates: number
  todayEnrollments: number
  todayExtensions: number
  todayApplications: number
  totalStaff: number
  activeAlerts: number
  placementReadyCount: number
}

/** Consolidated dashboard stats via single RPC call. */
export async function getDashboardStatsV2(client: DbClient = defaultDbClient): Promise<DashboardStatsV2> {
  const { data, error } = await client.rpc('get_dashboard_stats_v2')
  if (error) throw error
  return ((data ?? {}) as unknown as DashboardStatsV2)
}

/** Fetch all active (unacknowledged) alert instances with their rule names. */
export async function getActiveAlerts(client: DbClient = defaultDbClient): Promise<
  (OpsAlertInstanceRow & { rule?: { name: string; type: string } })[]
> {
  const { data, error } = await client
    .from('ops_alert_instances')
    .select('*, rule:rule_id(name, type)')
    .is('acknowledged_at', null)
    .order('created_at', { ascending: false })
    .limit(50)

  if (error) throw error
  return (data ?? []) as (OpsAlertInstanceRow & { rule?: { name: string; type: string } })[]
}

/** Acknowledge an alert instance. */
export async function acknowledgeAlert(
  alertId: string,
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<void> {
  const { error } = await client
    .from('ops_alert_instances')
    .update({ acknowledged_by: actor.userId, acknowledged_at: new Date().toISOString() })
    .eq('id', alertId)

  if (error) throw error
  await logActivity(withActivityActor(actor, {
    action: 'ACKNOWLEDGE_ALERT',
    entityType: 'ops_alert_instances',
    entityId: alertId,
  }), client)
}

/** Fetch recent bulk operations. */
export async function getBulkOperations(
  status?: string,
  limit = 20, client: DbClient = defaultDbClient
): Promise<BulkOperationRow[]> {
  let query = client
    .from('bulk_operations')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit)

  if (status) query = query.eq('status', status)

  const { data, error } = await query
  if (error) throw error
  return data ?? []
}

/** Create a new bulk operation job. */
export async function createBulkOperation(
  type: string,
  totalCount: number,
  filterCriteria: Record<string, unknown>,
  payload: Record<string, unknown>,
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<BulkOperationRow> {
  const { data, error } = await client
    .from('bulk_operations')
    .insert({
      type,
      status: 'pending',
      total_count: totalCount,
      filter_criteria: filterCriteria as unknown as Json,
      payload: payload as unknown as Json,
      created_by: actor.userId,
    } as TablesInsert<'bulk_operations'>)
    .select()
    .single()

  if (error) throw error
  await logActivity(withActivityActor(actor, {
    action: 'CREATE_BULK_OPERATION',
    entityType: 'bulk_operations',
    entityId: data.id,
    newValues: { type, total_count: totalCount },
  }), client)
  return data
}

/** Fetch system health snapshots for a metric. */
export async function getHealthSnapshots(
  metricName: string,
  hours = 24, client: DbClient = defaultDbClient
): Promise<SystemHealthSnapshotRow[]> {
  const since = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString()
  const { data, error } = await client
    .from('system_health_snapshots')
    .select('*')
    .eq('metric_name', metricName)
    .gte('created_at', since)
    .order('created_at', { ascending: true })

  if (error) throw error
  return data ?? []
}

/** Record a health snapshot (typically called by edge function or cron). */
export async function recordHealthSnapshot(
  metricName: string,
  metricValue: number,
  metadata?: Record<string, unknown>, client: DbClient = defaultDbClient
): Promise<void> {
  const { error } = await client.from('system_health_snapshots').insert({
    metric_name: metricName,
    metric_value: metricValue,
    metadata: (metadata ?? {}) as unknown as Json,
  })
  if (error) throw error
}

/** Count students matching bulk-operation filters. */
export async function countStudentsForBulkOp(filters: {
      courses: string[]
      expiringWithinDays: string
      statuses: string[]
    }, client: DbClient = defaultDbClient): Promise<number> {
  let query = client.from('students').select('*', { count: 'exact', head: true }).is('deleted_at', null)
  if (filters.courses.length) query = query.in('course', filters.courses)
  if (filters.statuses.length) query = query.in('status', filters.statuses)
  const days = parseInt(filters.expiringWithinDays, 10)
  if (!isNaN(days) && days > 0) {
    const today = new Date().toISOString().split('T')[0] ?? ''
    const future = new Date()
    future.setDate(future.getDate() + days)
    const futureIso = future.toISOString().split('T')[0] ?? ''
    query = query.gte('valid_till', today).lte('valid_till', futureIso)
  }
  const { count, error } = await query
  if (error) throw error
  return count ?? 0
}
